/** ***********
*
* <battery.component.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, distinctUntilChanged } from 'rxjs';
import { TRACKER_MAX_BATTERY_LIFE_MONTHS } from 'src/app/models/signal-source';
import { Tracker } from 'src/app/models/tracker';

enum BatteryLevelIcon {
    empty = 'battery-empty',
    '1/4' = 'battery-quarter',
    half = 'battery-half',
    '3/4' = 'battery-three-quarters',
    full = 'battery-full',
}

@Component({
    selector: 'app-battery',
    templateUrl: './battery.component.html',
    styleUrls: ['./battery.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BatteryComponent implements OnChanges {

    @Input() public tracker: Tracker;
    @Input()
    public get showBatteryIcon() {
        return this._showBatteryIcon;
    }
    public set showBatteryIcon(value: BooleanInput) {
        this._showBatteryIcon = coerceBooleanProperty(value);
    }

    public batteryIcon: IconProp;
    public batteryLabel: string;
    public TRACKER_MAX_BATTERY_LIFE_MONTHS = TRACKER_MAX_BATTERY_LIFE_MONTHS;

    private _showBatteryIcon = true;
    private _subscription = new Subscription();

    constructor(
        private _translate: TranslateService,
        private _cd: ChangeDetectorRef,
    ) {
        this._subscription.add(
            this._translate.onLangChange.pipe(
                distinctUntilChanged(),
            ).subscribe(
                () => this._setBatteryLabel()
            )
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.tracker?.previousValue !== changes.tracker?.currentValue) {
            this.batteryIcon = this._getBatteryIcon(changes.tracker.currentValue);
            this._setBatteryLabel();
            this._cd.markForCheck();
        }
    }

    private _getBatteryIcon(tracker: Tracker) {
        if (typeof tracker.batteryLevel !== 'number') {
            if (tracker.batteryLevel > TRACKER_MAX_BATTERY_LIFE_MONTHS) {
                return BatteryLevelIcon.full;
            }
            if (tracker.batteryLife < TRACKER_MAX_BATTERY_LIFE_MONTHS && tracker.batteryLife > 0) {
                return BatteryLevelIcon.half;
            }
            return BatteryLevelIcon.empty;
        } else {
            if (tracker.batteryLevel <= 0) {
                return BatteryLevelIcon.empty;
            }
            if (tracker.batteryLevel <= 25) {
                return BatteryLevelIcon['1/4'];
            }
            if (tracker.batteryLevel <= 50) {
                return BatteryLevelIcon.half;
            }
            if (tracker.batteryLevel <= 75) {
                return BatteryLevelIcon['3/4'];
            }
            return BatteryLevelIcon.full;
        }
    }

    private _setBatteryLabel() {
        if (typeof this.tracker.batteryLife === 'number') {
            if (this.tracker.batteryLife > TRACKER_MAX_BATTERY_LIFE_MONTHS) {
                this.batteryLabel = this._translate.instant('TRACKER_BATTERY_TIME_TOO_LARGE', {
                    unit: this._translate.instant('YEARS')?.toLowerCase(),
                    value: Math.floor(TRACKER_MAX_BATTERY_LIFE_MONTHS / 12),
                });
            } else {
                this.batteryLabel = `${this.tracker.batteryLife} ${this.tracker.batteryLife > 1 ?
                    this._translate.instant('MONTHS')?.toLowerCase() :
                    this._translate.instant('MONTH')?.toLowerCase()}`;
            }
        } else {
            return '';
        }
    }

}
